import { Controller } from "stimulus";
import { listboxAction, listboxIndex, selectedIndex, enterIndex, matchesQuery, searchStatus } from "../../training_grooming_calendar/listbox";

const NAV_KEYS = ["ArrowDown", "ArrowUp", "Enter", "Escape"];

export default class extends Controller {
  static targets = ["trigger", "list", "query", "clear", "options", "group", "option", "empty", "echo", "status", "dot", "value", "meta", "submit"];

  start() {
    this.close({ focus: false });
    this.select(null);
    this.triggerTarget.focus({ preventScroll: true });
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  open() {
    this.search("");
    this.listTarget.hidden = false;
    this.expand(true);
    this.element.dispatchEvent(new CustomEvent("training-grooming:service-open", { bubbles: true, detail: { list: this.listTarget } }));
    this.highlight(selectedIndex(this.visibleOptions()));
    if (!window.matchMedia("(pointer: coarse)").matches) this.queryTarget.focus({ preventScroll: true });
  }

  close({ focus = true } = {}) {
    if (!this.isOpen()) return;
    this.listTarget.hidden = true;
    this.expand(false);
    this.search("");
    if (focus) this.triggerTarget.focus({ preventScroll: true });
  }

  outside(event) {
    if (this.isOpen() && !this.triggerTarget.contains(event.target) && !this.listTarget.contains(event.target)) this.close({ focus: false });
  }

  filter() {
    this.search(this.queryTarget.value);
    this.statusTarget.textContent = searchStatus(this.visibleOptions().length, "service");
  }

  clear() {
    this.queryTarget.value = "";
    this.filter();
    this.queryTarget.focus({ preventScroll: true });
  }

  keydown(event) {
    if (!this.isOpen()) {
      if (listboxAction(event.key, false) !== "open") return;
      event.preventDefault();
      this.open();
      return;
    }
    if (event.key === "Tab") this.close({ focus: false });
    if (!NAV_KEYS.includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const options = this.visibleOptions();
    const active = options.indexOf(this.active);
    if (event.key === "Escape") {
      this.close();
    } else if (event.key === "Enter") {
      const option = options[enterIndex(active, options.length)];
      if (option) option.click();
    } else {
      this.highlight(listboxIndex(active, event.key, options.length));
    }
  }

  pick(event) {
    const option = event.currentTarget;
    this.select(option);
    this.close();
    this.element.dispatchEvent(new CustomEvent("training-grooming:service-picked", { detail: { duration: Number(option.dataset.duration) } }));
  }

  select(option) {
    this.optionTargets.forEach((node) => node.setAttribute("aria-selected", String(node === option)));
    this.element.classList.toggle("tg-qa-picked", !!option);
    this.dotTarget.style.background = option ? option.dataset.color : "";
    this.valueTarget.textContent = option ? option.dataset.name : this.valueTarget.dataset.placeholder;
    this.metaTarget.textContent = option ? option.dataset.meta : "";
    this.submitTarget.disabled = !option;
  }

  search(query) {
    if (this.queryTarget.value !== query) this.queryTarget.value = query;
    this.optionTargets.forEach((option) => {
      option.hidden = !matchesQuery(option.dataset.search, query);
    });
    this.groupTargets.forEach((group) => {
      group.hidden = !group.querySelector('[role="option"]:not([hidden])');
    });
    const count = this.visibleOptions().length;
    this.clearTarget.hidden = !query;
    this.optionsTarget.hidden = count === 0;
    this.emptyTarget.hidden = count > 0 || !query.trim();
    this.echoTarget.textContent = query;
    if (!query) this.statusTarget.textContent = "";
    this.highlight(-1);
  }

  highlight(index) {
    this.active = this.visibleOptions()[index] || null;
    this.optionTargets.forEach((option) => option.classList.toggle("tg-qa-option-active", option === this.active));
    if (this.active) {
      this.queryTarget.setAttribute("aria-activedescendant", this.active.id);
      this.active.scrollIntoView({ block: "nearest" });
    } else {
      this.queryTarget.removeAttribute("aria-activedescendant");
    }
  }

  expand(open) {
    [this.triggerTarget, this.queryTarget].forEach((node) => node.setAttribute("aria-expanded", String(open)));
  }

  isOpen() {
    return !this.listTarget.hidden;
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden);
  }
}
